import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const InstallPrompt = () => {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if running as PWA
    const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches || 
                               (window.navigator as any).standalone === true;
    setIsStandalone(isInStandaloneMode);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(iOS);

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      
      // Show install prompt after 3 seconds if not dismissed before
      const hasSeenPrompt = localStorage.getItem('installPromptDismissed');
      if (!hasSeenPrompt) {
        setTimeout(() => {
          setShowInstallPrompt(true);
        }, 3000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    }

    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    localStorage.setItem('installPromptDismissed', 'true');
  };

  // Don't show if already installed or dismissed
  if (isStandalone || (!showInstallPrompt && !isIOS)) {
    return null;
  }

  // iOS Install Instructions
  if (isIOS && showInstallPrompt) {
    return (
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-primary-500 shadow-2xl z-50 p-6 animate-slide-up">
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        <div className="max-w-md mx-auto">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 flex items-center justify-center bg-primary-100 rounded-2xl flex-shrink-0">
              <i className="ri-smartphone-line text-3xl text-primary-600"></i>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {t('installApp') || 'Install LoveConnect App'}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('installInstructions') || 'Tap the share button below and select "Add to Home Screen"'}
              </p>
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <div className="flex items-center gap-2">
                  <i className="ri-share-line text-xl text-primary-600"></i>
                  <span>{t('tapShare') || 'Tap Share'}</span>
                </div>
                <i className="ri-arrow-right-line text-gray-400"></i>
                <div className="flex items-center gap-2">
                  <i className="ri-add-box-line text-xl text-primary-600"></i>
                  <span>{t('addToHome') || 'Add to Home'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Android/Desktop Install Prompt
  if (deferredPrompt && showInstallPrompt) {
    return (
      <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-primary-600 to-pink-600 shadow-2xl z-50 p-6 animate-slide-up">
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-white/80 hover:text-white transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-2xl backdrop-blur-sm flex-shrink-0">
                <i className="ri-smartphone-line text-3xl text-white"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-1">
                  {t('installApp') || 'Install LoveConnect App'}
                </h3>
                <p className="text-sm text-white/90">
                  {t('installDescription') || 'Get the full app experience with offline access and notifications'}
                </p>
              </div>
            </div>
            <button
              onClick={handleInstallClick}
              className="px-8 py-3 bg-white text-primary-600 font-semibold rounded-full hover:bg-gray-100 transition-all shadow-lg whitespace-nowrap cursor-pointer flex items-center gap-2"
            >
              <i className="ri-download-line text-xl"></i>
              <span>{t('install') || 'Install'}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default InstallPrompt;
